/**
 * Iter122jw · NumeriUtiliAssegna — assegnazione fornitori per categoria di un condominio.
 * Ora mostrato nella Bacheca (tab "Numeri utili", vista Studio) e NON più in RAS.
 *
 * UX:
 * - Il condominio è selezionato dal componente padre (dropdown in NumeriUtiliStudio).
 * - Per ogni categoria:
 *   - Chip elencano i fornitori già assegnati (con X per rimuovere).
 *   - Un dropdown "Aggiungi fornitore" mostra solo i fornitori la cui `categorie[]`
 *     combacia con la categoria (Elettrico→Elettrica, Idraulico→Idraulica/Termoidraulica, ...).
 *   - Toggle "Mostra tutti i fornitori" per allargare la lista se il match non è sufficiente.
 * - Pulsante "Copia da altro condominio" per replicare le assegnazioni.
 */
import { useEffect, useMemo, useState } from "react";
import { Save, Copy, X, Plus } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

// Iter122jx · Con l'allineamento a core.CATEGORIE, la categoria "Numeri utili"
// coincide col nome della categoria fornitore. Match diretto case-insensitive,
// con fallback a substring bidirezionale per categorie custom o nomi legacy.
const LEGACY_TOKENS = {
  // vecchi nomi Numeri utili → chiavi fornitore
  elettrico: ["elettric"],
  idraulico: ["idraulic", "termoidraulic", "sanitari"],
  spurgo: ["spurg"],
  "termotecnico (ct)": ["riscaldamento", "caldaia", "termo"],
};

function fornitoreMatchCategoria(catNome, fornCategorie) {
  const nn = (catNome || "").trim().toLowerCase();
  const list = Array.isArray(fornCategorie) ? fornCategorie : [];
  if (!nn || list.length === 0) return false;
  const norm = (s) => (s || "").trim().toLowerCase();
  // Exact match (Elettrica === Elettrica)
  if (list.some((c) => norm(c) === nn)) return true;
  // Legacy mapping (Elettrico → Elettrica)
  if (LEGACY_TOKENS[nn]) {
    const tokens = LEGACY_TOKENS[nn];
    return list.some((c) => tokens.some((t) => norm(c).includes(t)));
  }
  // Fallback substring bidirezionale (per categorie custom)
  return list.some((c) => norm(c).includes(nn) || nn.includes(norm(c)));
}

export function NumeriUtiliAssegna({ condominioId }) {
  const [rows, setRows] = useState([]); // [{id (cat), nome, fornitore_ids, fornitori}]
  const [fornitori, setFornitori] = useState([]); // [{id, nome, telefono, email, categorie:[]}]
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Copia da altro condominio
  const [condomini, setCondomini] = useState([]);
  const [showCopy, setShowCopy] = useState(false);
  const [copySrcId, setCopySrcId] = useState("");
  // Per-categoria: mostra tutti i fornitori (bypassa filtro)
  const [showAll, setShowAll] = useState({}); // {catId: bool}

  useEffect(() => {
    if (!condominioId) return;
    setLoading(true);
    setDirty(false);
    setShowAll({});
    Promise.all([
      api.get(`/numeri-utili/condominio/${condominioId}`),
      api.get(`/fornitori`, { params: { limit: 1000 } }),
    ]).then(([a, b]) => {
      setRows(a.data);
      const arr = Array.isArray(b.data) ? b.data : (b.data?.fornitori || []);
      setFornitori(arr.map((f) => ({
        id: f.id,
        nome: f.nome || f.ragione_sociale || "—",
        telefono: f.telefono,
        email: f.email,
        categorie: f.categorie || [],
      })));
    }).catch(() => toast.error("Impossibile caricare le assegnazioni"))
      .finally(() => setLoading(false));
    // Iter122jv · Lista condomini per copia
    api.get("/condomini").then((r) => setCondomini(r.data || [])).catch(() => {});
  }, [condominioId]);

  const fornById = useMemo(() => {
    const m = new Map();
    fornitori.forEach((f) => m.set(f.id, f));
    return m;
  }, [fornitori]);

  const aggiungi = (catId, fid) => {
    if (!fid) return;
    setRows((prev) => prev.map((r) => {
      if (r.id !== catId) return r;
      const set = new Set(r.fornitore_ids || []);
      set.add(fid);
      return { ...r, fornitore_ids: Array.from(set) };
    }));
    setDirty(true);
  };
  const rimuovi = (catId, fid) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== catId) return r;
      const set = new Set(r.fornitore_ids || []);
      set.delete(fid);
      return { ...r, fornitore_ids: Array.from(set) };
    }));
    setDirty(true);
  };

  const copiaDa = async () => {
    if (!copySrcId) return;
    try {
      const { data: src } = await api.get(`/numeri-utili/condominio/${copySrcId}`);
      const mapSrc = {};
      src.forEach((s) => { mapSrc[s.id] = s.fornitore_ids || []; });
      setRows((prev) => prev.map((r) => ({ ...r, fornitore_ids: mapSrc[r.id] ? [...mapSrc[r.id]] : (r.fornitore_ids || []) })));
      setDirty(true);
      setShowCopy(false);
      setCopySrcId("");
      toast.success("Assegnazioni copiate. Ricordati di salvare per confermare.");
    } catch { toast.error("Copia non riuscita"); }
  };

  const salva = async () => {
    setSaving(true);
    try {
      await api.put(`/numeri-utili/condominio/${condominioId}`, {
        assegnazioni: rows.map((r) => ({ categoria_id: r.id, fornitore_ids: r.fornitore_ids || [] })),
      });
      toast.success("Assegnazioni salvate");
      setDirty(false);
      const { data } = await api.get(`/numeri-utili/condominio/${condominioId}`);
      setRows(data);
    } catch { toast.error("Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500 py-4" data-testid="nu-assegna-loading">Caricamento numeri utili…</div>;

  return (
    <div data-testid="nu-assegna-panel">
      {/* Header: copia da altro condominio + salva */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="ml-auto flex items-center gap-2">
          {!showCopy ? (
            <Button type="button" variant="outline" size="sm" data-testid="nu-copy-open"
              onClick={() => setShowCopy(true)}
              className="h-9 px-3 text-xs font-semibold border-2 border-brand/30 text-brand hover:bg-brand/5">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copia da altro condominio
            </Button>
          ) : (
            <div className="flex items-center gap-1.5 bg-brand/5 border-2 border-brand/30 rounded-lg p-1.5" data-testid="nu-copy-panel">
              <select data-testid="nu-copy-src" value={copySrcId}
                onChange={(e) => setCopySrcId(e.target.value)}
                className="h-8 px-2 border-2 border-slate-300 rounded-md bg-white text-xs font-semibold max-w-[220px] truncate">
                <option value="">— Seleziona condominio —</option>
                {condomini.filter((c) => c.id !== condominioId).map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
              <Button type="button" size="sm" data-testid="nu-copy-confirm"
                disabled={!copySrcId} onClick={copiaDa}
                className="h-8 px-2.5 text-xs font-semibold bg-brand hover:bg-brand-dark disabled:opacity-40">
                Copia
              </Button>
              <button type="button" data-testid="nu-copy-cancel"
                onClick={() => { setShowCopy(false); setCopySrcId(""); }}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-200">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((r) => {
          const assegnati = (r.fornitore_ids || []).map((id) => fornById.get(id)).filter(Boolean);
          const assegnatiSet = new Set(r.fornitore_ids || []);
          const filtroAttivo = !showAll[r.id];
          const candidatiBase = filtroAttivo
            ? fornitori.filter((f) => fornitoreMatchCategoria(r.nome, f.categorie))
            : fornitori;
          const candidati = candidatiBase
            .filter((f) => !assegnatiSet.has(f.id))
            .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
          const nMatch = fornitori.filter((f) => fornitoreMatchCategoria(r.nome, f.categorie)).length;
          return (
            <Card key={r.id} className="p-3 border-2 border-slate-200" data-testid={`nu-assegna-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="font-bold text-slate-800">{r.nome}</div>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none"
                  data-testid={`nu-showall-${r.id}`}>
                  <input type="checkbox" checked={!filtroAttivo}
                    onChange={(e) => setShowAll((s) => ({ ...s, [r.id]: e.target.checked }))} />
                  Mostra tutti i fornitori{filtroAttivo && nMatch >= 0 ? ` (filtrati ${nMatch})` : ""}
                </label>
              </div>

              {/* Chip fornitori già assegnati */}
              <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                {assegnati.length === 0 && (
                  <span className="text-xs italic text-slate-400">Nessun fornitore assegnato.</span>
                )}
                {assegnati.map((f) => (
                  <span key={f.id} data-testid={`nu-chip-${r.id}-${f.id}`}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-brand text-white text-xs font-semibold">
                    {f.nome}
                    <button type="button" onClick={() => rimuovi(r.id, f.id)}
                      data-testid={`nu-chip-remove-${r.id}-${f.id}`}
                      className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-white/20">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {/* Dropdown aggiungi */}
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-md bg-slate-100 text-slate-500"><Plus className="w-4 h-4" /></div>
                <select data-testid={`nu-add-select-${r.id}`}
                  value=""
                  onChange={(e) => { aggiungi(r.id, e.target.value); e.target.value = ""; }}
                  className="h-9 px-2 border-2 border-slate-300 rounded-lg bg-white text-sm flex-1 min-w-0">
                  <option value="">
                    {candidati.length === 0
                      ? (filtroAttivo ? "Nessun fornitore per questa categoria" : "Tutti i fornitori sono già assegnati")
                      : "— Aggiungi fornitore —"}
                  </option>
                  {candidati.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}{f.telefono ? ` · ${f.telefono}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button data-testid="nu-save-btn" disabled={!dirty || saving} onClick={salva} className="bg-brand hover:bg-brand-dark">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvo…" : "Salva assegnazioni"}
        </Button>
      </div>
    </div>
  );
}
